'use client';

import { useEffect, useRef, useState } from 'react';
import { useMicroSource } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { PopupHeader, padded } from '../chrome';

interface Item {
  id?: string | number;
  label: string;
  description?: string;
}

/** Debounce for progress-row writes so rapid toggling coalesces. */
const WRITE_DEBOUNCE_MS = 600;

/**
 * Interactive checklist. Each item toggles done; completing every
 * item fires {@code onComplete}.
 *
 * <p>Checked state persists on the learner's progress row: restored
 * from {@code progress.fetchItem(...).result.checklist.checked} on
 * open, written back (debounced, flushed on unmount) via
 * {@code progress.update({result: {checklist: {checked}}})} on every
 * toggle — the WS merges result keys, so other kinds' echoes survive.
 */
export function ChecklistRenderer({ item, onComplete }: RendererProps) {
  const micro = useMicroSource();
  const body = (item.body as { items?: Item[] }) ?? {};
  const items = body.items ?? [];
  const [checked, setChecked] = useState<Set<string>>(new Set());

  // Restore previously-checked ids from the progress row's result echo.
  useEffect(() => {
    if (!micro?.progress) return;
    let cancelled = false;
    void micro.progress
      .fetchItem(item.id)
      .then((row) => {
        if (cancelled || !row?.result) return;
        const checklist = row.result['checklist'];
        const stored =
          checklist && typeof checklist === 'object'
            ? (checklist as { checked?: unknown }).checked
            : undefined;
        if (Array.isArray(stored)) {
          setChecked(new Set(stored.map((v) => String(v))));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [micro, item.id]);

  // Debounced write; flush any pending write on unmount so the last
  // toggle before closing the popup isn't lost.
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<Set<string> | null>(null);
  const flush = () => {
    if (writeTimerRef.current != null) {
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending || !micro?.progress) return;
    void micro.progress
      .update({ itemId: item.id, result: { checklist: { checked: [...pending] } } })
      .catch(() => undefined); // persistence is best-effort
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);

  const persist = (next: Set<string>) => {
    if (!micro?.progress) return;
    pendingRef.current = next;
    if (writeTimerRef.current != null) clearTimeout(writeTimerRef.current);
    writeTimerRef.current = setTimeout(() => flushRef.current(), WRITE_DEBOUNCE_MS);
  };

  const toggle = (id: string) => {
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (next.size === items.length && items.length > 0) onComplete();
      persist(next);
      return next;
    });
  };

  if (items.length === 0) {
    return (
      <div style={padded}>
        <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      </div>
    );
  }

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      <ul
        style={{
          ...padded,
          listStyle: 'none',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {items.map((it, i) => {
          const id = String(it.id ?? i);
          const isChecked = checked.has(id);
          return (
            <li
              key={id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: 12,
                borderRadius: 8,
                background: isChecked
                  ? 'var(--color-success-50, #ecfdf5)'
                  : 'var(--color-surface-card, #fff)',
                border: `1px solid ${
                  isChecked
                    ? 'var(--color-success-200, #a7f3d0)'
                    : 'var(--color-border, rgba(0,0,0,0.08))'
                }`,
                cursor: 'pointer',
              }}
              onClick={() => toggle(id)}
            >
              <input type="checkbox" checked={isChecked} onChange={() => toggle(id)} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm, 14px)',
                    lineHeight: 1.4,
                    textDecoration: isChecked ? 'line-through' : 'none',
                  }}
                >
                  {it.label}
                </span>
                {/* The admin has always been able to write a per-item note;
                    until now the portal dropped it on the floor. Items with
                    no note render exactly as before — title only. */}
                {it.description && (
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs, 12px)',
                      lineHeight: 1.5,
                      color: 'var(--color-text-secondary, #71717a)',
                    }}
                  >
                    {it.description}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
